import React, { useState, useContext, useEffect, useCallback } from 'react';
import isEmail from 'validator/lib/isEmail';
import { useI18nContext } from '@ecg-marktplaats/js-react-i18n';

import Classes from './ResetPasswordComponent.module.scss';
import {
  Button,
  Card,
  TextField,
  UtilsBlock,
  ButtonsBlock,
  TextLarge,
  InfoInpage,
  Link,
  Title4,
} from '@hz-design-system/web-ui';
import EnvironmentContext from '../../../contexts/EnvironmentContext';
import { useRecaptcha, useRecaptchaHandler } from '../../../hooks/useRecaptcha';

import { executeRecaptcha, useEnterpriseRecaptcha } from '../../../hooks/useEnterpriseRecaptcha';
import { RECAPTCHA_EVENTS } from '../../../../../common/constants';

import resetPassword from '../repositories/resetPassword';
import useThreatMetrix from '../../../hooks/useThreatMetrix';

const ResetPasswordComponent = () => {
  const {
    xsrfToken,
    threatMetrix,
    expired,
    force,
    forcePwdReset,
    links: { loginRedirectUrl },
    recaptchaSiteKey,
    recaptchaScriptUrl,
    experiments: { accountDefenderEnabled = false } = {},
  } = useContext(EnvironmentContext);
  const { t } = useI18nContext();
  const [email, setEmail] = useState('');
  const [fieldError, setFieldError] = useState('');
  const [showMessageBlock, setShowMessageBlock] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [title, setTitle] = useState<string | null>(null);
  const [cta, setCta] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const TmxNoScript = useThreatMetrix(threatMetrix);

  // Load standard reCAPTCHA script for verification
  useRecaptcha(recaptchaScriptUrl, recaptchaSiteKey);
  // Load Enterprise reCAPTCHA script for tracking (if account defender is enabled)
  useEnterpriseRecaptcha(accountDefenderEnabled ? recaptchaSiteKey : undefined);

  // Track with Enterprise reCAPTCHA if enabled (fire and forget, do not use token for backend)
  useEffect(() => {
    if (accountDefenderEnabled && recaptchaSiteKey) {
      void executeRecaptcha(recaptchaSiteKey, RECAPTCHA_EVENTS.PASSWORD_RESET);
    }
    // Only on mount or when recaptchaSiteKey/accountDefenderEnabled changes
  }, [accountDefenderEnabled, recaptchaSiteKey]);

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setEmail(value);
    setIsLoading(false);
    setFieldError('');
  };

  const showSuccessMessage = () => {
    setTitle(t('pages.create_account.success.secondTitle'));
    setMessage(t('pages.reset_password.success_message', { email: email }));
    setCta(t('pages.reset_password.success.cta'));
    setShowMessageBlock(true);
  };

  const handleSubmit = useCallback(
    async (token: string) => {
      setIsLoading(true);
      if (!isEmail(email)) {
        setFieldError(t('pages.create_account.validation.email_invalid'));
        setIsLoading(false);
        return;
      } else {
        setFieldError('');
      }
      try {
        await resetPassword({ email, recaptchaToken: token, xsrfToken });
        showSuccessMessage();
      } catch {
        showSuccessMessage();
      }
      setIsLoading(false);
    },
    [email, xsrfToken, t],
  );

  const handleRecaptcha = useRecaptchaHandler({
    recaptchaSiteKey: recaptchaSiteKey || '',
    onSuccess: (token: string) => {
      void handleSubmit(token);
    },
    onError: () => {
      setIsLoading(false);
      setFieldError(t('pages.reset_password.validation.no_captcha_been_entered'));
    },
  });

  return (
    <div className={Classes.root}>
      <TmxNoScript />
      {force || forcePwdReset ? (
        <UtilsBlock pb="s">
          <Title4>{t('pages.forgot_password.page_header_password_reset')}</Title4>
        </UtilsBlock>
      ) : (
        <UtilsBlock pb="s">
          <Title4>{t('pages.forgot_password.page_header')}</Title4>
        </UtilsBlock>
      )}

      {expired && (
        <InfoInpage icon="info">
          <div className={Classes.infoMessage}>{t('pages.forgot_password.expired_notification')}</div>
        </InfoInpage>
      )}
      {forcePwdReset && (
        <InfoInpage icon="info">
          <div className={Classes.infoMessage}>{t('pages.forgot_password.forcePwdReset')}</div>
        </InfoInpage>
      )}
      {showMessageBlock ? (
        <Card padding="l" style={{ textAlign: 'left' }}>
          {title && <h2>{title}</h2>}
          {message && (
            <UtilsBlock pb="m">
              <TextLarge>{message}</TextLarge>
            </UtilsBlock>
          )}
          <TextLarge>{cta && <Link href={loginRedirectUrl}>{cta}</Link>}</TextLarge>
        </Card>
      ) : (
        <Card padding="l" style={{ textAlign: 'left' }}>
          <h2>{t('pages.forgot_password.subtitle')}</h2>
          <form>
            <UtilsBlock pb="s">
              <TextField
                labelText={t('pages.forgot_password.email_field_label')}
                type="email"
                isRequired={true}
                onChange={handleEmailChange}
                errorText={fieldError}
              />
            </UtilsBlock>
            <UtilsBlock pb="s">
              <p>{t('pages.forgot_password.description')}</p>
            </UtilsBlock>
            <ButtonsBlock>
              <Button.Primary type="button" disabled={isLoading} onClick={handleRecaptcha}>
                {t('pages.forgot_password.submit_button')}
              </Button.Primary>
            </ButtonsBlock>
          </form>
          {/* <ToastContainer /> */}
        </Card>
      )}
    </div>
  );
};

export default ResetPasswordComponent;
